import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useBranding, logoForLightSurface } from '../context/BrandingContext'
import { 
  Cpu, 
  Shield, 
  BarChart3
} from 'lucide-react'
import defaultLogoImage from '../assets/xtract-symbol-dark.png'

/**
 * Login Page - PLACEHOLDER FOR FUTURE REFERENCE
 * 
 * This page is kept in the codebase as a placeholder for future use cases.
 * 
 * Current Authentication Flow:
 * - SSO authentication is triggered automatically when any page is accessed
 * - Users are redirected to Microsoft Entra ID (Azure AD) for authentication
 * - After successful authentication, users are redirected back to the application
 * 
 * This page may be used in the future for:
 * - Custom branding during authentication flow
 * - Additional login options (e.g., guest access, different identity providers)
 * - Pre-authentication information display
 * - Error handling and user guidance
 * 
 * To re-enable manual login flow:
 * 1. Update AuthContext.jsx - remove auto-login effect
 * 2. Update App.jsx - restore Navigate to /login in PrivateRoute
 */
function Login() {
  const { login, isLoading } = useAuth()
  const { branding } = useBranding()
  const logoImage = branding.logo_url || defaultLogoImage
  const appName = branding.app_name || 'Xtract'
  const appSubtitle = branding.subtitle || 'Synapx'
  
  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 text-white flex-col justify-between p-12" style={{ backgroundColor: 'var(--brand-primary)' }}>
        <div>
          {/* Logo */}
          <div className="flex items-center gap-3 mb-16">
            <img src={logoImage} alt={appName} className="w-10 h-10 rounded-lg object-contain" />
            <div>
              <h1 className="text-xl font-semibold">{appName}</h1>
              <p className="text-xs text-white/60">{appSubtitle}</p>
            </div>
          </div>
          
          {/* Features */}
          <div className="space-y-8">
            <FeatureItem
              icon={Cpu}
              title="AI Powered Extraction"
              description="Advanced machine learning algorithms for precise document parsing and data extraction using Azure Document Intelligence."
            />
            <FeatureItem
              icon={Shield}
              title="Enterprise Security"
              description="Bank-grade encryption with role-based access control. Your data remains secure and compliant."
            />
            <FeatureItem
              icon={BarChart3}
              title="Real-time Analytics"
              description="Monitor processing performance with real-time dashboards and detailed workflow insights."
            />
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex items-center gap-8 pt-8 border-t border-white/10">
          <StatItem value="98.5%" label="Accuracy" />
          <StatItem value="50K+" label="Documents" />
          <StatItem value="24/7" label="Processing" />
        </div>
        
        <p className="text-xs text-white/40 mt-8">
          © {new Date().getFullYear()} Xtract by Synapx
          {' · '}
          <Link to="/privacy" className="underline hover:text-white/60">Privacy Policy</Link>
          {' · '}
          <Link to="/support" className="underline hover:text-white/60">Support</Link>
        </p>
      </div>
      
      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-950">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-800">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
              {/* The card is white in light mode, so the default Xtract symbol swaps to its light version. */}
              <img src={logoForLightSurface(logoImage)} alt={appName} className={`w-10 h-10 rounded-lg object-contain${logoForLightSurface(logoImage) !== logoImage ? ' dark:hidden' : ''}`} />
              {logoForLightSurface(logoImage) !== logoImage && (
                <img src={logoImage} alt={appName} className="hidden dark:block w-10 h-10 rounded-lg object-contain" />
              )}
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{appName}</h1>
              </div>
            </div>
            
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Sign in to Portal
              </h2>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                Access your document extraction dashboard
              </p>
            </div>
            
            <div className="space-y-4">
              <button
                onClick={login}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 text-white rounded-lg font-medium hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                      <path d="M10 0H0V10H10V0Z" fill="#F25022"/>
                      <path d="M21 0H11V10H21V0Z" fill="#7FBA00"/>
                      <path d="M10 11H0V21H10V11Z" fill="#00A4EF"/>
                      <path d="M21 11H11V21H21V11Z" fill="#FFB900"/>
                    </svg>
                    Sign in with Microsoft
                  </>
                )}
              </button>
              
              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                Use your Microsoft work or school account to sign in
              </p>
            </div>
            
            <p className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">
              By signing in, you agree to our{' '}
              <Link to="/privacy" className="underline hover:text-gray-600 dark:hover:text-gray-300">Privacy Policy</Link>
            </p>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Need help? <a href="#" className="text-navy-800 dark:text-navy-400 hover:underline">Contact Support</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function FeatureItem({ icon: Icon, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-white/80" />
      </div>
      <div>
        <h3 className="font-semibold mb-1">{title}</h3>
        <p className="text-sm text-white/60 leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

function StatItem({ value, label }) {
  return (
    <div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-white/60">{label}</p>
    </div>
  )
}

export default Login
